import { Link, createFileRoute } from '@tanstack/react-router'
import { motion } from 'motion/react'
import { Calculator, ChevronRight, Factory, Sparkles } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { PageTitle } from '@/components/PageTitle'

export const Route = createFileRoute('/_app/tools/')({
  component: ToolsLandingPage,
})

interface ToolCardProps {
  title: string
  description: string
  icon: React.ReactNode
  href: string
  badge?: string
  delay?: number
}

function ToolCard({
  title,
  description,
  icon,
  href,
  badge,
  delay = 0,
}: ToolCardProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay }}
    >
      <Link to={href} className="block group">
        <Card className="h-full border-border hover:border-primary/40 hover:shadow-md transition-all duration-200 group-hover:bg-primary/[0.02]">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0 group-hover:bg-primary/15 transition-colors">
                {icon}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-sm font-semibold leading-tight">
                    {title}
                  </h3>
                  {badge && (
                    <Badge
                      variant="secondary"
                      className="text-[10px] h-4 px-1.5"
                    >
                      {badge}
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  {description}
                </p>
              </div>
              <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors flex-shrink-0 mt-0.5" />
            </div>
          </CardContent>
        </Card>
      </Link>
    </motion.div>
  )
}

function ToolsLandingPage() {
  return (
    <div className="py-4 px-6 space-y-5">
      <PageTitle title="Tools" />
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
      >
        <h1 className="text-xl font-bold">Financial Planning Tools</h1>
        <p className="text-sm text-muted-foreground mt-0.5">
          Calculators and tools to support your advisory workflow
        </p>
      </motion.div>

      {/* Tools Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
        <ToolCard
          delay={0}
          title="RMD Calculator"
          description="Calculate Required Minimum Distributions and their tax impact for your clients. Includes year-by-year projections and heirs analysis."
          icon={<Calculator className="h-5 w-5 text-primary" />}
          href="/tools/rmd-calculator"
          badge="Available"
        />
        {import.meta.env.VITE_ENABLE_TAX_FACTORY === 'true' && (
          <ToolCard
            delay={0.05}
            title="Tax Factory"
            description="Visual simulator comparing Roth conversions vs no conversion. Show prospects the long-term tax savings impact with animated year-by-year projections."
            icon={<Factory className="h-5 w-5 text-primary" />}
            href="/tools/tax-factory"
            badge="New"
          />
        )}
      </div>

      {/* Coming Soon Banner */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.1 }}
      >
        <Card className="border-dashed border-muted-foreground/30 bg-muted/20">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                <Sparkles className="h-4 w-4 text-muted-foreground" />
              </div>
              <div>
                <p className="text-sm font-medium">More tools coming soon</p>
                <p className="text-xs text-muted-foreground">
                  We&apos;re building more financial planning tools to support
                  your practice. Stay tuned for updates.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  )
}
